import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import FroalaEditor from 'froala-editor';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/utils/ReactQuery';
import Froala from '@/components/shared/Froala';
import { insertIntoEditor }
  from '@/components/admin/SlideShow/Slide/Content/SlideObjectText/CustomFroalaCommands/FroalaListeners';
import ImageCaptionPopover from '@/components/admin/SectionEditor/ImageCaptionPopover';
import useLayout from '@/components/admin/SectionEditor/useLayout';
import { editImageAfterSubmit } from '@/components/admin/SectionEditor/SectionEditorUtils';
import {
  getCurrentImageSnippet,
  getImageModelIdFromElement,
  graphQlImageModelToTag,
} from '@/components/shared/Froala/froalaUtils';
import { MAIN_IDEAS } from '@/components/shared/Froala/plugins/MainIdeas';
import { MainIdeasStore } from '@/components/shared/Froala/plugins/MainIdeas/MainIdeasStore';
import useThemes from '@/hooks/api/useThemes';
import ITheme from '@/components/interfaces/Theme';
import cn from '@/utils/cn';
import SharedControls from './SharedControls';
import '@/components/admin/SectionEditor/froala-styles.scss';
import fetchExistingImages from './existingImages';

// Rendered by admin/reference_materials/_new_builder_form.html.erb, directly under the Diversity
// input. It sits outside the tab panels on purpose: theme and layout are reference-material-wide, so
// one copy per reading tab would put two `reference_material[theme_id]` inputs in the form.
export const SHARED_CONTROLS_ID = 'reading-shared-controls';

interface ReadingEditorProps {
  level: string;
  locale?: string;
  modelId: number | string;
  name: string;
  showSharedControls?: boolean;
  text: string;
  themeId: number;
  userType: UserType;
}

const ReadingEditor = ({
  level,
  locale = 'en',
  modelId,
  name,
  showSharedControls = true,
  text: initialText,
  themeId,
  userType,
}: ReadingEditorProps) => {
  const [text, setText] = useState(initialText);
  const [editor, setEditor] = useState<typeof FroalaEditor>();
  const [currentThemeId, setCurrentThemeId] = useState<ITheme['id']>(themeId);
  const [element, setElement] = useState<HTMLElement>(null);

  // AIDEV-NOTE: PLAT-17622/PLAT-17624 — the plugin's DOM scope, same idea as the section builder's
  // tabs. Locale is part of it because the English and Spanish tabs are two live editors in one
  // form; a shared selector would have main-ideas mode in one tab toggling the other's SVG.
  // A reference material only has level 'a' today; PLAT-17626 adds AI bands.
  const levelSelector = `.level-${level}-${locale}`;
  const store = MainIdeasStore.get(levelSelector);
  const isActive = store(state => state.isActive);

  const { layout, onChange: changeLayout } = useLayout({ editor, onChange: setText });
  const { data: themes } = useThemes();
  const [controlsTarget, setControlsTarget] = useState<HTMLElement>(null);

  const themeClass = themes.find(theme => theme.id === currentThemeId)?.css_name;

  // The tab panels are mounted by SectionTabs after the page itself renders, so the target is always
  // in the DOM by now. Reading it into state rather than during render keeps the portal out of the
  // first paint if a caller ever mounts this editor without one.
  useEffect(() => {
    if (!showSharedControls) return;

    setControlsTarget(document.getElementById(SHARED_CONTROLS_ID));
  }, [showSharedControls]);

  return (
    <>
      <ImageCaptionPopover editorInstance={editor} element={element} />
      {controlsTarget && ReactDOM.createPortal(
        <SharedControls
          currentThemeId={currentThemeId}
          layout={layout}
          onChangeLayout={changeLayout}
          onChangeTheme={setCurrentThemeId}
        />,
        controlsTarget,
      )}

      <div
        className={cn(
          { 'two-column': layout === 'two_column' },
          { 'main-ideas-editing-mode': isActive },
          'new-section-text',
        )}
      >
        <div
          className={cn({
            'display-main-ideas': isActive,
            [themeClass]: !!currentThemeId,
          })}
        >
          <Froala
            activePlugins={[MAIN_IDEAS.commandName]}
            AddImageProps={{
              // AIDEV-NOTE: PLAT-17696 — AddImage skips afterSubmit once duplicates come back, so the
              // merge decision is the only thing that inserts the image. Without this the upload is
              // saved and silently never reaches the editor. /merge_images returns every image model
              // for the primary image (other reference materials, sections, slides), and modelId is a
              // String over GraphQL but a Number over REST — hence the scoped, stringified match.
              afterMergeDuplicates: (imageModels, editorInstance) => {
                const merged = imageModels.find(({ modelId: id, modelType }) => (
                  modelType === 'ReferenceMaterial' && id?.toString() === modelId?.toString()
                ));

                if (!merged) return;

                insertIntoEditor(editorInstance, graphQlImageModelToTag(merged));
              },
              afterSubmit: (imageModel, editorInstance) => {
                insertIntoEditor(editorInstance, graphQlImageModelToTag(imageModel));
              },
              dontMergeCallback: (imageModel, editorInstance) => {
                insertIntoEditor(editorInstance, graphQlImageModelToTag(imageModel));
              },
              getExistingImages: () => fetchExistingImages(modelId),
              reuseExistingImage: true,
            }}
            className={cn('!tw-w-full tw-mt-4', `level-${level}-${locale}`)}
            customConfig={{
              enter: FroalaEditor.ENTER_P,
              height: 725,
              lineBreakerTags: [...FroalaEditor.DEFAULTS.lineBreakerTags, '.captions'],
              pastePlain: true,
              pluginsEnabled: [
                'align', 'colors', 'charCounter', 'codeBeautifier', 'codeView', 'emoticons',
                'imageManager', 'fontFamily', 'fontSize', 'inlineStyle', 'link',
                'image', 'video', 'table', 'paragraphStyle', 'paragraphFormat', 'quote',
                'lists',
              ],
              tabSpaces: 6,
            }}
            EditImageProps={{
              afterSubmit: editImageAfterSubmit(),
            }}
            eventHandlers={[
              {
                click: (e) => {
                  const target = e.target as HTMLElement;
                  const id = getImageModelIdFromElement(target);

                  if (!id) return;

                  const currentSnippet = getCurrentImageSnippet(id);
                  if (!currentSnippet.getAttribute('data-snippet').includes('CAPTION')) return;

                  setElement(currentSnippet as HTMLElement);
                },
              },
            ]}
            modelId={modelId}
            modelType="ReferenceMaterial"
            name={name}
            pluginProps={{
              [MAIN_IDEAS.commandName]: {
                selector: levelSelector,
              },
            }}
            setEditor={setEditor}
            text={text}
            userType={userType}
          />
        </div>
      </div>
    </>
  );
};

const ReadingEditorWrapper = (props: ReadingEditorProps) => (
  <QueryClientProvider client={queryClient}>
    <ReadingEditor {...props} />
  </QueryClientProvider>
);

export default ReadingEditorWrapper;
